import { initials, RING_COLORS, RING_INKS } from "../../maps/tokens";
import type { LiveCampaignHubMember, LiveCampaignHubSceneEntry } from "../../types/generated/live";

export type Dot = { accountId: string; initials: string; label: string; background?: string; ink?: string };

// The mapa on the table, or the one a pending switch is moving the table to (`pending` is table_switch.ts's).
export function activeScene(scenes: LiveCampaignHubSceneEntry[] | null, pending?: string | null) {
	return scenes?.find((scene) => scene.id === pending) ?? scenes?.find((scene) => scene.active);
}

// What a pending write shows over a mapa; a deleted one is gone.
export type ScenePatch = { name?: string; gone?: true };

// The mapas as pending writes will leave them. A deleted mapa on the table hands the table to the next in the list, else the
// previous, as the server does (position, then id).
export function withScenes(scenes: LiveCampaignHubSceneEntry[] | null, overlay: Record<string, ScenePatch>): LiveCampaignHubSceneEntry[] | null {
	if (!scenes) return scenes;
	const kept = scenes
		.filter((scene) => !overlay[scene.id]?.gone)
		.map((scene) => (overlay[scene.id]?.name ? { ...scene, name: overlay[scene.id].name as string } : scene));
	const lost = scenes.findIndex((scene) => scene.active && overlay[scene.id]?.gone);
	if (lost < 0 || kept.some((scene) => scene.active)) return kept;
	const live = (scene: LiveCampaignHubSceneEntry) => !overlay[scene.id]?.gone;
	const next = scenes.slice(lost + 1).find(live) ?? scenes.slice(0, lost).reverse().find(live);
	return kept.map((scene) => (scene.id === next?.id ? { ...scene, active: true } : scene));
}

// The mapa that takes the table when `id` is deleted: the next in the list, else the previous.
export function heir<T extends { id: string }>(scenes: T[], id: string) {
	const at = scenes.findIndex((scene) => scene.id === id);
	return scenes[at + 1] ?? (at > 0 ? scenes[at - 1] : undefined);
}

// The masters first, then by name without the handle's @, as the board's presence row orders them; accounts that are
// not members are left out.
export function presentDots(present: Iterable<string>, members: LiveCampaignHubMember[]): Dot[] {
	const byAccount = new Map(members.map((member) => [member.account_id, member]));
	const name = (member: LiveCampaignHubMember) => (member.name ?? member.handle ?? "").replace(/^@/, "");
	return [...present]
		.flatMap((id) => byAccount.get(id) ?? [])
		.sort((a, b) => Number(b.role !== "player") - Number(a.role !== "player") || name(a).localeCompare(name(b)))
		.map((member) => ({
			accountId: member.account_id,
			initials: initials(name(member)) || "?",
			label: name(member) || "?",
			background: member.color ? RING_COLORS[member.color] : undefined,
			ink: member.color ? RING_INKS[member.color] : undefined,
		}));
}

// `/mapas/:id/…` with the scene filled in.
export const sceneUrl = (template: string, id: string) => template.replace(":id", id);
